import { useCallback, useState } from "react";
import { planMetadata } from "src/services/plans";

import FeatureList from "src/components/FeatureList/FeatureList";
import Notification from "src/components/Notification/Notification";
import LicenseImage from "src/components/LicenseImage/LicenseImage";
import { License, LicenseType } from "src/components/pages/Home/license";

import { EligibilityState, Eligibility } from "./Eligibility";

import commonStyles from "./ChoosePlan.module.css";

const getPersonalButtonLabel = (currentLicense: License | null) => {
  switch (currentLicense?.type) {
    case LicenseType.personal:
      return "Current Lens subscription";
    default:
      return "Choose";
  }
};

export interface Props {
  loading?: boolean;
  license: License | null;
  onChoosePlan(planCode: "personal"): Promise<boolean>;
}

const ChoosePersonal: React.FC<Props> = ({
  loading,
  license,
  onChoosePlan
}) => {
  const planData = planMetadata.personal;

  // TODO: Positive choice should probably be stored somewhere?
  const [eligibleForPersonal, setEligibleForPersonal] = useState<EligibilityState>("unset");

  const handleEligibilityChange = useCallback(async (next: EligibilityState) => {
    setEligibleForPersonal(next);

    if (next === "pending-response") {
      try {
        await onChoosePlan("personal");
        setEligibleForPersonal("eligible");
      } catch (err) {
        setEligibleForPersonal("error");
      }
    }
  }, [onChoosePlan]);

  return (
    <>
      <section className={commonStyles.planInfo}>
        <LicenseImage type="personal" className={commonStyles.licenseImage} />
        <div className={commonStyles.price} aria-label="Price"><span>{planMetadata.personal.price.monthly.text}</span></div>
        <div className={commonStyles.eligibilityDescription}>
          {planMetadata.personal.planSideNote.map((paragraph) => (<span key={paragraph}>{paragraph}</span>))}
        </div>
        {
          eligibleForPersonal === "error" && (
            <Notification type="flash" level="error" message="Something went wrong. Please try again later." />
          )
        }
        <Eligibility
          className={commonStyles.actions}
          current={license?.type === LicenseType.personal}
          discreet={Boolean(license)}
          disabled={license?.type === LicenseType.pro || license?.type === LicenseType.proTrial}
          loading={Boolean(loading)}
          buttonLabel={getPersonalButtonLabel(license)}
          state={eligibleForPersonal}
          onChange={handleEligibilityChange}
        />
      </section>
      <section>
        <FeatureList
          className={commonStyles.featureList}
          title={`Included in Lens ${planData.nameShort}`}
          features={planData.features}
        />
      </section>
    </>
  );
};

export default ChoosePersonal;
